import React, {useState} from 'react';
import ReactApexChart from 'react-apexcharts';
import {Dropdown} from 'react-bootstrap';

const DailyChart = ({data}) => {
    const [timeRange, setTimeRange] = useState('30');
    const [chartType, setChartType] = useState('area');
    const [filteredData, setFilteredData] = useState(data.slice(-30)); // Start with last 30 days

    const chartTypes = [
        {id: 'area', name: 'Area Chart', icon: 'bi-area-chart'},
        {id: 'line', name: 'Line Chart', icon: 'bi-graph-up'},
        {id: 'bar', name: 'Bar Chart', icon: 'bi-bar-chart'}
    ];

    const filterData = (days) => {
        return days === 'All' ? data : data.slice(-days); // Slice from the end of the array
    };

    const handleTimeRangeChange = (days) => {
        setTimeRange(days);
        setFilteredData(filterData(days === 'All' ? data.length : parseInt(days)));
    };

    const baseOptions = {
        chart: {
            height: 350,
            toolbar: {show: false},
            zoom: {enabled: false},
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800
            }
        },
        colors: ['#4e73df', '#1cc88a'],
        dataLabels: {enabled: false},
        stroke: {
            curve: 'smooth',
            width: 2
        },
        xaxis: {
            type: 'category',
            categories: filteredData.map((row) => row.day),
            labels: {
                rotate: -45,
                style: {fontSize: '12px', colors: '#6c757d'}
            },
            axisBorder: {show: true, color: '#e9ecef'},
            axisTicks: {show: true, color: '#e9ecef'}
        },
        yaxis: {
            title: {
                text: 'Messages',
                style: {fontWeight: 'normal', color: '#6c757d', fontSize: '12px'}
            },
            min: 0,
            forceNiceScale: true
        },
        grid: {
            borderColor: '#f8f9fa',
            strokeDashArray: 3
        },
        tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            y: {formatter: (val) => `${val} messages`}
        },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            markers: {width: 12, height: 12, radius: 6}
        },
        responsive: [{
            breakpoint: 600,
            options: {
                chart: {height: 300},
                xaxis: {
                    labels: {
                        rotate: -45,
                        style: {fontSize: '10px'}
                    }
                }
            }
        }]
    };

    const getChartOptions = () => {
        switch (chartType) {
            case 'area':
                return {
                    ...baseOptions,
                    chart: {...baseOptions.chart, type: 'area', stacked: true},
                    fill: {
                        type: 'solid',
                        opacity: 0.7
                    }
                };
            case 'line':
                return {
                    ...baseOptions,
                    chart: {...baseOptions.chart, type: 'line'},
                    markers: {size: 5},
                    stroke: {width: 3}
                };
            case 'bar':
                return {
                    ...baseOptions,
                    chart: {...baseOptions.chart, type: 'bar', stacked: true},
                    plotOptions: {
                        bar: {
                            horizontal: false,
                            borderRadius: 6,
                            borderRadiusApplication: 'end',
                            columnWidth: '60%'
                        }
                    },
                    fill: {type: 'solid', opacity: 1}
                };
            default:
                return baseOptions;
        }
    };

    const series = [
        {
            name: 'Sent Messages',
            data: filteredData.map((row) => row.sent)
        },
        {
            name: 'Received Messages',
            data: filteredData.map((row) => row.received)
        }
    ];

    return (
        <div className="card shadow h-100">
            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                <div className="card-title mb-0">
                    <i className="bi bi-calendar-day text-primary me-2"></i>
                    Daily Message Activity
                </div>

                <div className="d-flex">
                    <Dropdown className="me-2">
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <i className={`bi ${chartTypes.find(t => t.id === chartType)?.icon} me-1`}></i>
                            {chartTypes.find(t => t.id === chartType)?.name}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {chartTypes.map((type) => (
                                <Dropdown.Item
                                    key={type.id}
                                    active={chartType === type.id}
                                    onClick={() => setChartType(type.id)}
                                >
                                    <i className={`bi ${type.icon} me-2`}></i>
                                    {type.name}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>

                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                            Last {timeRange} days
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleTimeRangeChange('7')}>Last 7 days</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleTimeRangeChange('14')}>Last 14 days</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleTimeRangeChange('30')}>Last 30 days</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleTimeRangeChange('All')}>All time</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            <div className="card-body p-0">
                <ReactApexChart
                    options={getChartOptions()}
                    series={series}
                    type={chartType === 'bar' ? 'bar' : chartType}
                    height={350}
                />
            </div>

            <div className="card-footer bg-white border-0 small text-muted">
                <i className="bi bi-info-circle me-1"></i>
                Hover over chart elements to see detailed message counts
            </div>
        </div>
    );
};

export default DailyChart;